import React, { useState, useEffect, useRef } from "react";

/**
 * Cinematic mission briefing intro
 * Auto-plays before the mission welcome screen
 * Supports audio voiceover
 */
export function MissionBriefing({ tour, onComplete }) {
  const [stage, setStage] = useState(0); // 0: fade in, 1: classification, 2: briefing text, 3: fade out
  const [visibleChars, setVisibleChars] = useState(0);
  const [titleDecrypted, setTitleDecrypted] = useState('');
  const [animationSkipped, setAnimationSkipped] = useState(false);
  const audioRef = useRef(null);

  // Stage 0 -> 1: Transition after accepting transmission
  useEffect(() => {
    if (stage === 0) {
      console.log('[MissionBriefing] Stage 0: Setting timer to advance to stage 1');
      const timer = setTimeout(() => {
        console.log('[MissionBriefing] Timer fired: Advancing to stage 1');
        setStage(1);
      }, 1000);
      return () => {
        console.log('[MissionBriefing] Cleanup: Clearing stage 0->1 timer');
        clearTimeout(timer);
      };
    }
  }, [stage]);

  // Stage 1 -> 2: Transition after classification appears
  useEffect(() => {
    if (stage === 1 && !animationSkipped) {
      console.log('[MissionBriefing] Stage 1: Setting timer to advance to stage 2');
      const timer = setTimeout(() => {
        console.log('[MissionBriefing] Timer fired: Advancing to stage 2');
        setStage(2);
        // Auto-play audio if available
        if (tour.briefingAudio && audioRef.current) {
          console.log('[MissionBriefing] Starting audio playback');
          audioRef.current.play().catch(e => {
            console.log('Audio autoplay blocked:', e);
          });
        }
      }, 2000); // 2 seconds after stage 1 starts
      return () => {
        console.log('[MissionBriefing] Cleanup: Clearing stage 1->2 timer');
        clearTimeout(timer);
      };
    }
  }, [stage, animationSkipped, tour.briefingAudio]);

  // Decrypt mission title with scrambling effect
  useEffect(() => {
    if (stage === 1 && !animationSkipped) {
      const targetText = tour.briefing?.title || tour.missionName || '';
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      const decryptDuration = 2000; // 2 seconds total
      const updateInterval = 50; // Update every 50ms
      const totalUpdates = decryptDuration / updateInterval;
      let updateCount = 0;

      const decryptInterval = setInterval(() => {
        updateCount++;

        // On final update, just set the complete text
        if (updateCount >= totalUpdates) {
          setTitleDecrypted(targetText);
          clearInterval(decryptInterval);
          return;
        }

        const progress = updateCount / totalUpdates;

        // When we're close to the end, just reveal everything
        if (progress >= 0.95) {
          setTitleDecrypted(targetText);
          return;
        }

        // Use ceil to ensure we reveal characters progressively
        const revealedLength = Math.ceil(targetText.length * progress);

        let newText = '';
        for (let i = 0; i < targetText.length; i++) {
          if (i < revealedLength) {
            // Character is revealed
            newText += targetText[i];
          } else if (targetText[i] === ' ') {
            // Keep spaces as spaces
            newText += ' ';
          } else {
            // Still scrambling
            newText += chars[Math.floor(Math.random() * chars.length)];
          }
        }

        setTitleDecrypted(newText);
      }, updateInterval);

      return () => clearInterval(decryptInterval);
    }
  }, [stage, tour.briefing?.title, tour.missionName, animationSkipped]);

  // Type out briefing text character by character
  useEffect(() => {
    if (stage === 2 && tour.briefing?.setup && !animationSkipped) {
      // Join all lines with newline characters to get total text
      const fullText = tour.briefing.setup.join('\n');
      const totalChars = fullText.length;
      let currentChar = 0;

      const typeInterval = setInterval(() => {
        if (currentChar < totalChars) {
          currentChar++;
          setVisibleChars(currentChar);
        } else {
          clearInterval(typeInterval);
        }
      }, 59); // Type each character every 59ms

      return () => clearInterval(typeInterval);
    }
  }, [stage, tour.briefing?.setup, animationSkipped]);

  const skipToEnd = () => {
    // Stop audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Set flag to stop all animations
    setAnimationSkipped(true);
    // Jump to final stage and show all content
    setStage(2);
    setTitleDecrypted(tour.briefing?.title || tour.missionName || '');
    const fullText = tour.briefing?.setup?.join('\n') || '';
    setVisibleChars(fullText.length);
  };

  const continueMission = () => {
    // Stop audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setStage(3);
    setTimeout(() => onComplete(), 500);
  };

  return (
    <div
      className="fixed z-50 overflow-y-auto"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#000000',
        animation: stage === 3 ? 'fadeOut 0.5s ease-out forwards' : 'fadeIn 1s ease-in',
        padding: 'calc(env(safe-area-inset-top, 0px) + 1.5rem) 1.5rem calc(env(safe-area-inset-bottom, 0px) + 1.5rem)'
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes typeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .line-appear {
          animation: typeIn 0.4s ease-out forwards;
        }
        @keyframes scanlines {
          0% { background-position: 0 0; }
          100% { background-position: 0 100%; }
        }
        .scanlines {
          background: repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.15) 0px,
            transparent 1px,
            transparent 2px,
            rgba(0, 0, 0, 0.15) 3px
          );
          background-size: 100% 4px;
          animation: scanlines 8s linear infinite;
        }
        .classified-border {
          position: relative;
        }
        .classified-border::before {
          content: '';
          position: absolute;
          top: -10px;
          left: -10px;
          right: -10px;
          bottom: -10px;
          border: 2px solid #EF4444;
          opacity: 0.3;
        }
        .classified-border::after {
          content: '';
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          border: 1px solid #EF4444;
          opacity: 0.5;
        }
      `}</style>

      {/* Scanlines overlay for CRT effect */}
      <div className="scanlines fixed inset-0 pointer-events-none opacity-20" />

      <div className={`w-full max-w-3xl mx-auto relative min-h-full flex flex-col py-8 ${stage < 2 ? 'justify-center' : 'justify-start'}`}>
        {/* Classification banner */}
        {stage >= 1 && (
          <div
            className="text-center mb-8"
            style={{
              animation: 'typeIn 0.8s ease-out'
            }}
          >
            <div
              className="inline-block px-8 py-3 classified-border"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid #EF4444'
              }}
            >
              <div
                className="font-mono text-2xl font-bold tracking-widest"
                style={{ color: '#EF4444' }}
              >
                {tour.briefing?.classification || 'CLASSIFIED'}
              </div>
            </div>
          </div>
        )}

        {/* Mission title - with decryption effect */}
        {stage >= 1 && (
          <h1
            className="text-center font-mono text-4xl md:text-5xl font-bold mb-8 tracking-wide"
            style={{
              color: '#10B981',
              textShadow: '0 0 20px rgba(16, 185, 129, 0.5)',
              animation: 'typeIn 0.8s ease-out 0.3s both',
              letterSpacing: '0.1em'
            }}
          >
            {titleDecrypted || tour.briefing?.title || tour.missionName}
          </h1>
        )}

        {/* Briefing content */}
        {stage >= 2 && (
          <div
            className="mx-auto max-w-2xl p-8 rounded-lg mb-8"
            style={{
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              boxShadow: '0 0 40px rgba(16, 185, 129, 0.1)'
            }}
          >
            <div className="space-y-3 font-mono text-base leading-relaxed">
              {tour.briefing?.setup?.map((line, idx) => {
                // Calculate character positions for this line
                const previousLines = tour.briefing.setup.slice(0, idx);
                const charsBeforeLine = previousLines.reduce((sum, l) => sum + l.length + 1, 0); // +1 for newline
                const charsInLine = line.length;
                const lineEndPos = charsBeforeLine + charsInLine;

                // Determine what to show for this line
                let displayText = '';
                if (visibleChars >= lineEndPos) {
                  // Full line is visible
                  displayText = line;
                } else if (visibleChars > charsBeforeLine) {
                  // Partial line is visible
                  displayText = line.substring(0, visibleChars - charsBeforeLine);
                }
                // else line hasn't started yet

                // Check if this line is a header (starts with ##)
                const isHeader = line.startsWith('##');

                return (
                  <div
                    key={idx}
                    style={{
                      color: line === '' ? 'transparent' : isHeader ? '#10B981' : '#E5E7EB',
                      height: line === '' ? '0.5em' : 'auto',
                      minHeight: '1.5rem',
                      fontSize: isHeader ? '1.1em' : '1em',
                      fontWeight: isHeader ? 'bold' : 'normal',
                      letterSpacing: isHeader ? '0.1em' : 'normal',
                      textTransform: isHeader ? 'uppercase' : 'none',
                      marginTop: isHeader ? '1.5em' : '0',
                      marginBottom: isHeader ? '0.5em' : '0'
                    }}
                  >
                    {displayText.replace(/^##\s*/, '') || '\u00A0'}
                    {/* Blinking cursor on active line */}
                    {visibleChars > charsBeforeLine && visibleChars <= lineEndPos && (
                      <span style={{ animation: 'blink 1s infinite' }}>▋</span>
                    )}
                  </div>
                );
              })}
            </div>
            <style>{`
              @keyframes blink {
                0%, 49% { opacity: 1; }
                50%, 100% { opacity: 0; }
              }
            `}</style>
          </div>
        )}

        {/* Action button - appears after all text is typed */}
        {stage >= 2 && visibleChars >= (tour.briefing?.setup?.join('\n').length || 0) && (
          <div
            className="flex justify-center"
            style={{
              animation: 'typeIn 0.6s ease-out'
            }}
          >
            <button
              onClick={continueMission}
              className="px-6 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.4)',
                cursor: 'pointer'
              }}
            >
              Accept Mission →
            </button>
          </div>
        )}

        {/* Skip Animation button - show during animation */}
        {stage >= 1 && stage < 3 && visibleChars < (tour.briefing?.setup?.join('\n').length || 0) && (
          <div
            className="absolute right-4"
            style={{
              top: 'max(1rem, calc(env(safe-area-inset-top, 0px) + 1rem))'
            }}
          >
            <button
              onClick={skipToEnd}
              className="px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all"
              style={{
                background: 'rgba(71, 85, 105, 0.5)',
                color: '#ffffff',
                border: '1px solid rgba(71, 85, 105, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(71, 85, 105, 0.5)';
              }}
            >
              Skip Animation
            </button>
          </div>
        )}

        {/* Audio element - hidden */}
        {tour.briefingAudio && (
          <audio
            ref={audioRef}
            src={tour.briefingAudio}
          />
        )}
      </div>
    </div>
  );
}
